import React, { useState, useEffect } from "react";
import {
  CircularProgress,
  Snackbar,
  Alert,
  Avatar,
  InputBase,
  Select,
  MenuItem,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

import { CustomBtn } from "../../../components/Common/CustomBtn";
import { getInitials } from "../../../components/ui/Gapper";
import { colors } from "../../../theme/theme";
import { useAdminPractitioners } from "./AdminPractitioners.hooks";
import { useQuickLinksItems } from "../../../context/QuickLinksContext";
import AddPractitionerModal from "./AddPractitionerModal";
import PractitionerDetailModal from "./PractitionerDetailModal";
import ReviewChangesModal from "./ReviewChangesModal";
import ArchivePractitionerDialog from "./ArchivePractitionerDialog";
import {
  RootBox,
  HeaderRow,
  PageHeading,
  PageSubheading,
  StatsRow,
  StatCard,
  StatValue,
  StatLabel,
  ToolbarCard,
  TableCard,
  ScrollBody,
  HeaderCell,
  RowGrid,
  PractitionerCell,
  NameLine,
  Name,
  SubText,
  CellText,
  LicensedBadge,
  PendingEditBadge,
  StatusPill,
  LoadingBox,
  EmptyBox,
} from "./AdminPractitioners.styles";

const STATUS_ICON = {
  approved: <CheckCircleIcon sx={{ fontSize: 15 }} />,
  pending: <AccessTimeIcon sx={{ fontSize: 15 }} />,
  rejected: <CancelIcon sx={{ fontSize: 15 }} />,
  archived: <Inventory2OutlinedIcon sx={{ fontSize: 15 }} />,
};

const AdminPractitioners = () => {
  const {
    loading,
    items,
    stats,
    statusFilter,
    setStatusFilter,
    search,
    setSearch,
    moderate,
    addPractitioner,
    publishEdit,
    rejectEdit,
    archivePractitioner,
    restorePractitioner,
    reload,
    snack,
    closeSnack,
  } = useAdminPractitioners();

  const { reviewPractitionerId, setReviewPractitionerId } = useQuickLinksItems();

  const [addOpen, setAddOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);

  // A notification / email deep link asked to review a specific practitioner.
  useEffect(() => {
    if (reviewPractitionerId) {
      setReviewId(reviewPractitionerId);
      setReviewPractitionerId(null);
    }
  }, [reviewPractitionerId, setReviewPractitionerId]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Reset to first page when the result set changes (filter/search/reload).
  useEffect(() => {
    setPage(0);
  }, [statusFilter, search, items.length]);

  const paged = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openMenu = (e, row) => {
    setMenuAnchor(e.currentTarget);
    setMenuRow(row);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const STAT_CARDS = [
    { key: "all", label: "Total", value: stats.total, color: colors.primary },
    { key: "approved", label: "Approved", value: stats.approved, color: "#409d45" },
    { key: "pending", label: "Pending", value: stats.pending, color: "#C77A22" },
    { key: "rejected", label: "Rejected", value: stats.rejected, color: "#C0392B" },
    // Archived accounts are excluded from Total — they are not part of the roster.
    { key: "archived", label: "Archived", value: stats.archived || 0, color: colors.textLight },
  ];

  const fmtDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "—");

  return (
    <RootBox>
      <HeaderRow>
        <div>
          <PageHeading>Practitioners</PageHeading>
          <PageSubheading>Manage and approve practitioner accounts</PageSubheading>
        </div>
        <CustomBtn text="Add Practitioner" Icon={PersonAddAltIcon} onClick={() => setAddOpen(true)} noMb noMt />
      </HeaderRow>

      {/* Stat cards double as status filters */}
      <StatsRow>
        {STAT_CARDS.map((c) => (
          <StatCard
            key={c.key}
            $active={statusFilter === c.key}
            onClick={() => setStatusFilter(c.key)}
          >
            <StatValue $color={c.color}>{c.value}</StatValue>
            <StatLabel>{c.label}</StatLabel>
          </StatCard>
        ))}
      </StatsRow>

      {/* Search + status filter */}
      <ToolbarCard>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 240 }}>
          <SearchIcon sx={{ color: colors.textLight }} />
          <InputBase
            placeholder="Search by name, email, or specialization..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, fontFamily: "'Quicksand', sans-serif" }}
          />
          {search && (
            <IconButton size="small" onClick={() => setSearch("")} aria-label="Clear search">
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
        </div>
        <Select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 160, fontFamily: "'Quicksand', sans-serif", borderRadius: 2 }}
        >
          <MenuItem value="all">All Statuses</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
          <MenuItem value="archived">Archived</MenuItem>
        </Select>
      </ToolbarCard>

      <TableCard>
        <RowGrid sx={{ borderBottom: `1px solid ${colors.headerBg}` }}>
          <HeaderCell>Practitioner</HeaderCell>
          <HeaderCell>Specialization</HeaderCell>
          <HeaderCell>Location</HeaderCell>
          <HeaderCell>Status</HeaderCell>
          <HeaderCell>Joined</HeaderCell>
          <span />
        </RowGrid>

        {loading ? (
          <LoadingBox>
            <CircularProgress sx={{ color: colors.secondary }} />
          </LoadingBox>
        ) : items.length === 0 ? (
          <EmptyBox>No practitioners found.</EmptyBox>
        ) : (
          <ScrollBody>
          {paged.map((p) => (
            <RowGrid key={p.id}>
              <PractitionerCell>
                <Avatar
                  src={p.avatar || undefined}
                  imgProps={{ style: { objectPosition: p.avatarPosition || "center" } }}
                  sx={{ width: 40, height: 40, bgcolor: colors.secondary, fontSize: 14 }}
                >
                  {getInitials(p.name)}
                </Avatar>
                <div style={{ minWidth: 0 }}>
                  <NameLine>
                    <Name>{p.name || "—"}</Name>
                    {p.licensed && <LicensedBadge>Licensed</LicensedBadge>}
                    {p.hasPendingEdit && (
                      <PendingEditBadge>
                        <RateReviewOutlinedIcon sx={{ fontSize: 13 }} />
                        Changes pending
                      </PendingEditBadge>
                    )}
                  </NameLine>
                  <SubText>{p.email}</SubText>
                </div>
              </PractitionerCell>
              <CellText>{p.specialization || "—"}</CellText>
              <CellText>{p.location || "—"}</CellText>
              <StatusPill $status={p.status}>
                {STATUS_ICON[p.status]}
                {p.status}
              </StatusPill>
              <CellText>{fmtDate(p.joined)}</CellText>
              <IconButton
                size="small"
                onClick={(e) => openMenu(e, p)}
                sx={{ width: 36, height: 36, flexShrink: 0, borderRadius: "50%", justifySelf: "center" }}
              >
                <MoreVertIcon />
              </IconButton>
            </RowGrid>
          ))}
          </ScrollBody>
        )}

        {!loading && items.length > 0 && (
          <TablePagination
            component="div"
            count={items.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{
              borderTop: `1px solid ${colors.headerBg}`,
              ".MuiTablePagination-toolbar, .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows, .MuiTablePagination-select":
                { fontFamily: "'Quicksand', sans-serif" },
            }}
          />
        )}
      </TableCard>

      {/* Row actions */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {/* An archived practitioner only offers "Restore" — moderating or editing
            a disabled account makes no sense and the backend rejects it. */}
        {menuRow?.status === "archived" ? (
          <MenuItem
            onClick={() => {
              restorePractitioner(menuRow.id);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <UnarchiveOutlinedIcon fontSize="small" sx={{ color: "#409d45" }} />
            </ListItemIcon>
            <ListItemText>Restore</ListItemText>
          </MenuItem>
        ) : (
          <>
        <MenuItem
          onClick={() => {
            setDetailId(menuRow.id);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View / Edit</ListItemText>
        </MenuItem>
        {menuRow?.hasPendingEdit && (
          <MenuItem
            onClick={() => {
              setReviewId(menuRow.id);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <RateReviewOutlinedIcon fontSize="small" sx={{ color: "#C77A22" }} />
            </ListItemIcon>
            <ListItemText>Review Changes</ListItemText>
          </MenuItem>
        )}
        {menuRow?.status !== "approved" && (
          <MenuItem
            onClick={() => {
              moderate(menuRow.id, "approved");
              closeMenu();
            }}
          >
            <ListItemIcon>
              <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#409d45" }} />
            </ListItemIcon>
            <ListItemText>Approve</ListItemText>
          </MenuItem>
        )}
        {menuRow?.status !== "rejected" && (
          <MenuItem
            onClick={() => {
              moderate(menuRow.id, "rejected");
              closeMenu();
            }}
          >
            <ListItemIcon>
              <HighlightOffIcon fontSize="small" sx={{ color: "#C0392B" }} />
            </ListItemIcon>
            <ListItemText>Reject</ListItemText>
          </MenuItem>
        )}
        {menuRow?.status !== "pending" && (
          <MenuItem
            onClick={() => {
              moderate(menuRow.id, "pending");
              closeMenu();
            }}
          >
            <ListItemIcon>
              <AccessTimeIcon fontSize="small" sx={{ color: "#C77A22" }} />
            </ListItemIcon>
            <ListItemText>Set Pending</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setArchiveTarget(menuRow);
            closeMenu();
          }}
          sx={{ borderTop: `1px solid ${colors.headerBg}`, mt: 0.5, pt: 1 }}
        >
          <ListItemIcon>
            <ArchiveOutlinedIcon fontSize="small" sx={{ color: "#C77A22" }} />
          </ListItemIcon>
          <ListItemText sx={{ color: "#C77A22" }}>Archive</ListItemText>
        </MenuItem>
          </>
        )}
      </Menu>

      <AddPractitionerModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreate={addPractitioner}
      />

      <PractitionerDetailModal
        practitionerId={detailId}
        open={!!detailId}
        onClose={() => setDetailId(null)}
        onModerate={moderate}
        onSaved={(id) => {
          // Admin edit is staged, not published — open the self-check review.
          reload();
          if (id) setReviewId(id);
        }}
      />

      <ArchivePractitionerDialog
        open={!!archiveTarget}
        practitioner={archiveTarget || {}}
        onClose={() => setArchiveTarget(null)}
        onConfirm={archivePractitioner}
      />

      <ReviewChangesModal
        practitionerId={reviewId}
        open={!!reviewId}
        onClose={() => setReviewId(null)}
        onPublish={publishEdit}
        onReject={rejectEdit}
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </RootBox>
  );
};

export default AdminPractitioners;
